// ============================================================================
// QuoteBrainBar: the one strip that starts and follows a Quote Brain run on
// the open job (docs/quote-brain/DESIGN.md 2.10, section 8; BUILD-PLAN 2a
// step 2, the minimum UI before the review screen).
// ----------------------------------------------------------------------------
//   - "Write the quote" at Stage 1, for a business whose Quote Brain is on.
//     A run
//     another part of the app starts shows up here. The server starts a new
//     job's run by itself (runs list `autoStart`), so while it will the bar
//     shows how far it is (reading the files, then starting) and no button:
//     Peter, 2026-10-31, "after clicking Process Files, I still feel like I
//     need to press Write now". Once Stage 1 has read the files and written
//     the summary, the bar asks the server to start now (live-start) rather
//     than on its next minute. Beside it a quiet "Write it yourself" hands
//     the job to the builder (Fast-Track, so the server never starts on it),
//     and on such a job "Let the Quote Brain write it" hands it back (O1).
//     Once the job has a quote
//     it is a quiet "Write again" (lucid 017 O1): a second version held for
//     Adopt / Keep mine, not the next step.
//   - While a run is live: one plain notice that the Quote Brain is writing
//     the quote, its step, elapsed time and Stop (no turns or spend: those
//     are Peter's report page's). The run is marked live for the old-writer
//     guards and for Stage 4, Stage 5 and the Assistant, which leave the items
//     alone meanwhile (src/utils/quote-brain-project.ts, Peter's O1).
//   - A fresh job's quote is written straight in, and the job reloads. With
//     unsaved changes on this tab, it asks first, and until the tab loads it a
//     stale-save conflict on the job offers only to load it (review round 2,
//     W1 to W3: replacing the saved version would erase the quote).
//   - A run the bar cannot read three times running no longer locks the job:
//     one line says so and editing is open again (W6).
//   - A result held aside (a re-run on a job with a quote, or items added
//     during the run) opens the Adopt / Keep mine panel, at Stage 5 by itself.
//   - Paused: Continue or Stop. Failed or stopped: the reason, Try again, and
//     for a fresh job the run had taken, Write it yourself. Nothing is left
//     without a way forward.
//   - The Assistant's QuoteMaker actions (start, stop, Adopt, Keep mine, who
//     writes the job) run through this bar's own handlers, by the command
//     bridge (services/quotemaker-writer-commands.ts), so they save, follow and
//     mark the run exactly as a click does.
//   - A business never sees a run's spend, cap, turns or model: the server
//     strips them (showCosts), and a stop reads as plain words
//     (run-status-text.js quoteMakerStoppedText).
// Renders nothing when there is nothing to say. A light toolbar, so a
// <div role="banner">, never <header> (CLAUDE.md frontend rule 2).
// ============================================================================

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    adoptQuoteBrainRun,
    continueQuoteBrainRun,
    getQuoteBrainComparison,
    getQuoteBrainRun,
    getQuoteBrainStatus,
    keepMineQuoteBrainRun,
    listQuoteBrainRuns,
    LIVE_RUN_STATUSES,
    releaseQuoteBrainRun,
    startQuoteBrainLiveNow,
    startQuoteBrainRun,
    stopQuoteBrainRun,
    type QuoteBrainAutoStart,
    type QuoteBrainComparison,
    type QuoteBrainRunDetail,
    type QuoteBrainStatus,
} from '../../services/quote-brain-runs';
import {
    getQuoteBrainBusinessMode, markQuoteBrainJobReleased, markQuoteBrainRunLive, markQuoteBrainWriteUnloaded, noteQuoteBrainAutoStart,
    noteQuoteBrainBusinessMode, QUOTE_BRAIN_WROTE_WHILE_EDITING,
} from '../../utils/quote-brain-project';
import { QuoteBrainComparePanel } from './QuoteBrainComparePanel';
import { QuoteBrainJourney } from './QuoteBrainJourney';
import { quoteBrainJourney } from '../../services/quote-brain-journey';
import { noteQuoteBrainWorking } from '../../utils/quote-brain-working';
import { PROJECT_WORKFLOW_MODES, WRITE_IT_YOURSELF_TIP, HAND_BACK_TIP } from '../../services/project-workflow';
import { quoteMakerStoppedText } from '../../../backend/src/shared/quote-brain/run-status-text.js';
import {
    QUOTEMAKER_WRITER_COMMAND_EVENT,
    type QuoteMakerWriterCommandDetail,
    type QuoteMakerWriterOutcome,
} from '../../services/quotemaker-writer-commands';
import { Icons } from '../ui/Icon';
import './quote-brain.css';

const POLL_MS = 4000;
// Reads of the followed run that fail in a row before the bar stops treating it as live (W6).
const UNREACHABLE_AFTER_FAILURES = 3;
// How often a new job under "on" looks for a run started elsewhere.
const WAIT_POLL_MS = 10000;
const DISMISSED_KEY = 'qm.quoteBrain.dismissedRuns';
const BUSY_OUTCOME = { ok: false, message: 'The QuoteMaker bar is busy with another action. Ask again in a moment.' };
const NOTHING_HELD_OUTCOME = { ok: false, message: 'No new version from QuoteMaker is waiting on this job.' };

export interface QuoteBrainBarProps {
    projectId: string | null;
    stage: number;
    /** True for a job with no old-pipeline work yet (isQuoteBrainNewJob). */
    newJob: boolean;
    /** True when the job has items, a Work Method or a costing sheet, so a run's result is held for the panel. */
    hasQuoteWork?: boolean;
    /** True while this tab's Stage 1 reads the job's files or writes its summary (the server's start waits for both). */
    preparing?: boolean;
    /** This tab's Stage 1 count of files read, of files sent, for the journey line's fill. */
    files?: { processed: number; total: number } | null;
    /** True while this tab's Stage 1 reads or processes the job's files (the Files step). */
    filesProcessing?: boolean;
    /** True while this tab writes the job's summary (the Summary step). */
    summarizing?: boolean;
    /** The items the job holds, said once the Quote Brain's quote is in. */
    itemCount?: number;
    /** Opens Stage 4, offered once the Quote Brain's quote is in. */
    onOpenScope?: () => void;
    /** True once the job has its summary, the server's signal that Stage 1 is done. */
    hasSummary?: boolean;
    /** True when the builder writes this job's scope by hand (Fast-Track): the server never starts a run on it. */
    manual?: boolean;
    /** Sets who writes this job (its workflow mode) and saves it. False when the save failed. */
    onWriterChange?: (mode: string) => Promise<boolean>;
    /** The project version this tab holds (the save token). */
    getSaveToken: () => string | null;
    /** Save anything pending, so the run starts from what is on screen. False when the save failed. */
    ensureSaved: () => Promise<boolean>;
    /** False while this tab holds unsaved changes or a Quote Run drives it. */
    canReloadNow: () => boolean;
    /** Load the job again from the server. */
    reloadProject: () => Promise<unknown>;
    showConfirm: (options: Record<string, any>) => void;
    setToast: (toast: Record<string, any>) => void;
}

function readDismissed(): string[] {
    try { return JSON.parse(localStorage.getItem(DISMISSED_KEY) || '[]'); } catch { return []; }
}

function dismiss(runId: string) {
    try { localStorage.setItem(DISMISSED_KEY, JSON.stringify([...readDismissed(), runId].slice(-50))); } catch { /* per-viewer convenience only */ }
}

function elapsed(fromIso: string | null, toIso: string | null, now: number): string {
    const from = fromIso ? Date.parse(fromIso) : NaN;
    if (!Number.isFinite(from)) return '';
    const to = toIso ? Date.parse(toIso) : now;
    const s = Math.max(0, Math.round((to - from) / 1000));
    const m = Math.floor(s / 60);
    return m > 0 ? `${m}m ${String(s % 60).padStart(2, '0')}s` : `${s}s`;
}

/** True when the version this tab holds is older than the one the run wrote. */
function tabIsBehind(token: string | null, writtenUpdatedAt: string | null): boolean {
    const written = writtenUpdatedAt ? Date.parse(writtenUpdatedAt) : NaN;
    if (!Number.isFinite(written)) return false;
    const held = token ? Date.parse(token) : NaN;
    return !Number.isFinite(held) || held < written;
}

export function QuoteBrainBar({ projectId, stage, newJob, hasQuoteWork = false, preparing = false, files = null, filesProcessing, summarizing = false, itemCount = 0, onOpenScope, hasSummary = false, manual = false, onWriterChange, getSaveToken, ensureSaved, canReloadNow, reloadProject, showConfirm, setToast }: QuoteBrainBarProps) {
    const [status, setStatus] = useState<QuoteBrainStatus | null>(null);
    const [detail, setDetail] = useState<QuoteBrainRunDetail | null>(null);
    const [busy, setBusy] = useState(false);
    const [now, setNow] = useState(() => Date.now());
    const [compare, setCompare] = useState<QuoteBrainComparison | null>(null);
    const [compareOpen, setCompareOpen] = useState(false);
    // A comparison read that failed, and a counter that reads it again.
    const [compareError, setCompareError] = useState<string | null>(null);
    const [compareTry, setCompareTry] = useState(0);
    // A written quote this tab has not loaded yet (it held unsaved changes, or the load failed).
    const [reloadPending, setReloadPending] = useState<{ runId: string; writtenAt: string | null } | null>(null);
    // The followed run could not be read UNREACHABLE_AFTER_FAILURES times running, so the job is no longer marked live (W6).
    const [unreachable, setUnreachable] = useState(false);
    // Whether the server starts this job's run by itself (the live tick), read with the runs list.
    const [autoStart, setAutoStart] = useState<QuoteBrainAutoStart | null>(null);
    // The job the server would not start after its summary (it holds no summary signal for it): its start is by hand.
    const [noSignal, setNoSignalState] = useState<string | null>(null);
    const noSignalRef = useRef<string | null>(null);
    const setNoSignal = useCallback((id: string | null) => { noSignalRef.current = id; setNoSignalState(id); }, []);
    // The job whose finished reading the bar already reported to the server (live-start), once per reading.
    const reportedRef = useRef<string | null>(null);
    const projectRef = useRef(projectId);
    projectRef.current = projectId;
    // The latest props, read by timers and callbacks (never a pinned closure).
    const propsRef = useRef({ getSaveToken, ensureSaved, canReloadNow, reloadProject, showConfirm, setToast, onWriterChange });
    propsRef.current = { getSaveToken, ensureSaved, canReloadNow, reloadProject, showConfirm, setToast, onWriterChange };
    const deliveredRef = useRef<string | null>(null);
    // The run this bar last saw live, and the one whose quote it then saw put into the job, with the stage it was shown on.
    // The finished line stays until it is dismissed or the builder moves to another stage.
    const sawLiveRef = useRef<string | null>(null);
    const [finished, setFinished] = useState<{ runId: string; stage: number } | null>(null);
    const stageRef = useRef(stage);
    stageRef.current = stage;
    // The run this bar follows. `n` restarts the poll for the same run after
    // Continue or Try again.
    const [follow, setFollow] = useState<{ runId: string; n: number } | null>(null);
    // The server's showCosts (the QuoteMaker operator only). Absent means false, so nothing costed shows.
    const [showCosts, setShowCosts] = useState(false);

    useEffect(() => {
        let alive = true;
        getQuoteBrainStatus()
            .then((s) => {
                if (!alive) return;
                setStatus(s);
                noteQuoteBrainBusinessMode(s?.mode, s?.ready);
            })
            .catch(() => {
                if (!alive) return;
                setStatus(null);
                // The bar shows nothing now, so Stage 1 keeps Analyse (quoteBrainStage1Route). The mode stays: W0 is unchanged.
                noteQuoteBrainBusinessMode(getQuoteBrainBusinessMode(), false);
            });
        return () => { alive = false; };
    }, [projectId]);

    const followLatest = useCallback(async (forProject: string): Promise<boolean> => {
        const { runs, autoStart: start } = await listQuoteBrainRuns(forProject);
        if (projectRef.current === forProject) {
            setAutoStart(start || null);
            if (start?.state !== 'expected' && noSignalRef.current === forProject) setNoSignal(null);
            // Stage 1 hides Analyse only while this bar promises the server's start (quoteBrainStage1Route).
            const stalled = start?.state === 'expected' && noSignalRef.current === forProject;
            noteQuoteBrainAutoStart(forProject, stalled ? null : start?.state || null);
        }
        const latest = (runs || []).find((r) => r.gateMode === 'on');
        if (projectRef.current !== forProject || !latest) return false;
        if (latest.releasedAt) markQuoteBrainJobReleased(forProject, true);
        setFollow((f) => (f?.runId === latest.id ? f : { runId: latest.id, n: 0 }));
        return true;
    }, [setNoSignal]);

    // The job's latest run that can touch the job (shadow runs never do).
    useEffect(() => {
        setDetail(null);
        setFollow(null);
        setCompare(null);
        setCompareOpen(false);
        setReloadPending(null);
        setUnreachable(false);
        setAutoStart(null);
        setNoSignal(null);
        setFinished(null);
        reportedRef.current = null;
        deliveredRef.current = null;
        sawLiveRef.current = null;
        if (!projectId) return undefined;
        followLatest(projectId).catch(() => { /* the bar stays quiet; nothing is blocked */ });
        return () => {
            markQuoteBrainRunLive(projectId, false);
            noteQuoteBrainAutoStart(projectId, null);
            noteQuoteBrainWorking(projectId, null);
        };
    }, [projectId, followLatest, setNoSignal]);

    // Stage 1 reads the files again (more were added), or the job changes hands: the reading is reported again when it
    // is done and the job is the Quote Brain's.
    useEffect(() => {
        if (!preparing && !manual) return;
        reportedRef.current = null;
        if (noSignalRef.current) setNoSignal(null);
    }, [preparing, manual, setNoSignal]);

    // Stage 1 has read the files and written the summary: the server starts the run now instead of on its next minute,
    // and the bar follows it at once. A server with no summary for the job (no signal) cannot start it by itself, so the
    // bar offers "Write the quote" and Stage 1 keeps Analyse. A failed report changes nothing: the tick starts it.
    const readingDone = !!projectId && newJob && !manual && !follow && status?.mode === 'on' && !!status.ready
        && !preparing && hasSummary && autoStart?.state === 'expected';
    useEffect(() => {
        if (!readingDone || !projectId || reportedRef.current === projectId) return;
        reportedRef.current = projectId;
        const forProject = projectId;
        startQuoteBrainLiveNow(forProject)
            .then(async ({ autoStart: start }) => {
                if (projectRef.current !== forProject) return;
                if (start?.state === 'expected') {
                    setNoSignal(forProject);
                    setAutoStart(start);
                    noteQuoteBrainAutoStart(forProject, null);
                    return;
                }
                await followLatest(forProject);
            })
            .catch(() => { /* the server's tick starts it within the minute */ });
    }, [readingDone, projectId, followLatest, setNoSignal]);

    // A new job under "on" with no run yet: look for one started elsewhere.
    const waiting = !!projectId && !follow && newJob && status?.mode === 'on';
    useEffect(() => {
        if (!waiting || !projectId) return undefined;
        const forProject = projectId;
        const t = setInterval(() => { followLatest(forProject).catch(() => { /* try again next tick */ }); }, WAIT_POLL_MS);
        return () => clearInterval(t);
    }, [waiting, projectId, followLatest]);

    // Load the version the run wrote. A load that leaves the tab behind it did not load it.
    const loadWritten = useCallback(async (runId: string, writtenAt: string | null) => {
        try {
            await propsRef.current.reloadProject();
            if (writtenAt && tabIsBehind(propsRef.current.getSaveToken(), writtenAt)) throw new Error('the written version did not load');
            setReloadPending(null);
            dismiss(runId);
            // The finished journey line says so, with the way to the items (no toast as well: said once).
            setFinished({ runId, stage: stageRef.current });
        } catch {
            setReloadPending({ runId, writtenAt });
            propsRef.current.setToast({ message: 'Your quote is written, but this tab couldn\'t load it. Press Load it above.', type: 'error' });
        }
    }, []);

    // Poll the followed run while it is live or its result is on the way.
    useEffect(() => {
        if (!follow || !projectId) return undefined;
        let alive = true;
        let timer: ReturnType<typeof setTimeout> | null = null;
        const forProject = projectId;
        let failures = 0;
        const poll = async () => {
            try {
                const d = await getQuoteBrainRun(follow.runId);
                if (!alive || projectRef.current !== forProject) return;
                failures = 0;
                setUnreachable(false);
                setShowCosts(d.showCosts === true);
                setDetail(d);
                const isLive = LIVE_RUN_STATUSES.has(d.run.status);
                // The quote so far first, so Stage 4 has it the moment the run is marked live (lucid 019 O2).
                noteQuoteBrainWorking(forProject, isLive ? d.working : null);
                markQuoteBrainRunLive(forProject, isLive, d.run.id);
                if (isLive) sawLiveRef.current = d.run.id;
                const awaitingDelivery = d.run.status === 'complete' && !!d.candidate && !d.candidate.delivery;
                if (isLive || awaitingDelivery) {
                    timer = setTimeout(() => { void poll(); }, POLL_MS);
                    return;
                }
                if (d.run.releasedAt) markQuoteBrainJobReleased(forProject, true);
                const c = d.candidate;
                if (c?.delivery === 'written' && deliveredRef.current !== follow.runId) {
                    deliveredRef.current = follow.runId;
                    if (!tabIsBehind(propsRef.current.getSaveToken(), c.writtenUpdatedAt)) {
                        // This tab already holds the quote. It is news only when the bar saw the run working.
                        if (sawLiveRef.current === follow.runId) setFinished({ runId: follow.runId, stage: stageRef.current });
                        return;
                    }
                    if (propsRef.current.canReloadNow()) await loadWritten(follow.runId, c.writtenUpdatedAt);
                    else setReloadPending({ runId: follow.runId, writtenAt: c.writtenUpdatedAt });
                }
            } catch {
                if (!alive) return;
                // A run the bar cannot read no longer locks the job, since a lock whose Stop fails is stuck (W6). The bar
                // keeps reading it, and a read that works marks it again from its record.
                failures += 1;
                if (failures >= UNREACHABLE_AFTER_FAILURES && projectRef.current === forProject) {
                    noteQuoteBrainWorking(forProject, null);
                    markQuoteBrainRunLive(forProject, false);
                    setUnreachable(true);
                }
                timer = setTimeout(() => { void poll(); }, POLL_MS * 2);
            }
        };
        void poll();
        return () => {
            alive = false;
            if (timer) clearTimeout(timer);
        };
    }, [follow, projectId, loadWritten]);

    // A written quote this tab still has not loaded. It is gone once the tab holds that version, however it got there (the
    // update notice's Load, a reload).
    const pending = reloadPending && (!reloadPending.writtenAt || tabIsBehind(getSaveToken(), reloadPending.writtenAt)) ? reloadPending : null;
    const pendingRunId = pending?.runId || null;
    // Meanwhile the job is marked, so a stale-save conflict on it offers only to load the Quote Brain's quote (W1 to W3).
    useEffect(() => {
        if (!projectId || !pendingRunId) return undefined;
        markQuoteBrainWriteUnloaded(projectId, pendingRunId);
        return () => { markQuoteBrainWriteUnloaded(projectId, null); };
    }, [projectId, pendingRunId]);

    const run = detail?.run || null;
    const live = !!run && LIVE_RUN_STATUSES.has(run.status);
    const stamped = !!run?.pipelineStampedAt && !run.releasedAt;
    const held = !!run && detail?.candidate?.delivery === 'held' && !detail.candidate.decision;

    // The panel's figures, read when a held result is shown.
    useEffect(() => {
        if (!held || !run) { setCompare(null); setCompareError(null); return undefined; }
        let alive = true;
        setCompareError(null);
        getQuoteBrainComparison(run.id)
            .then((c) => { if (alive) setCompare(c); })
            .catch((err: any) => {
                if (!alive) return;
                setCompare(null);
                setCompareError(err?.message || 'The comparison could not be read.');
            });
        return () => { alive = false; };
    }, [held, run?.id, run?.updatedAt, compareTry]); // eslint-disable-line react-hooks/exhaustive-deps

    // At Stage 5 the panel opens by itself.
    useEffect(() => {
        if (held && stage === 5) setCompareOpen(true);
    }, [held, stage]);

    useEffect(() => {
        if (!live) return undefined;
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, [live]);

    // The finished line goes once the builder moves on to another stage (Open the scope among them).
    useEffect(() => {
        if (finished && stage !== finished.stage) setFinished(null);
    }, [stage, finished]);

    // Follow a run the builder just started, continued or is stopping. The banner shows that run at once, with its notice
    // and Stop, and the job is marked live only from a run record that says so: a mark with no banner would lock the
    // items with no way to see why or stop it (never stuck or silently blocked). A poll that fails keeps the last record
    // on screen, so its Stop stays there, until the run cannot be read three times running (W6).
    const beginFollowing = useCallback((started: QuoteBrainRunDetail['run']) => {
        setUnreachable(false);
        setDetail((d) => ({ run: started, candidate: d?.run?.id === started.id ? d.candidate : null, turns: d?.run?.id === started.id ? d.turns : [] }) as QuoteBrainRunDetail);
        // Stage 4 shows nothing of an earlier run or an earlier rate book: the run's own next read fills it.
        if (projectRef.current) noteQuoteBrainWorking(projectRef.current, null);
        if (projectRef.current) markQuoteBrainRunLive(projectRef.current, LIVE_RUN_STATUSES.has(started.status), started.id);
        setFollow((f) => ({ runId: started.id, n: (f?.n || 0) + 1 }));
    }, []);

    const start = useCallback(async (): Promise<QuoteMakerWriterOutcome> => {
        const id = projectRef.current;
        if (!id || busy) return BUSY_OUTCOME;
        setBusy(true);
        try {
            if (!(await propsRef.current.ensureSaved())) {
                const message = 'The job couldn\'t be saved, so writing didn\'t start. Save it and try again.';
                propsRef.current.setToast({ message, type: 'error' });
                return { ok: false, message };
            }
            const { run: started, showCosts: costs } = await startQuoteBrainRun(id);
            setShowCosts(costs === true);
            beginFollowing(started);
            return { ok: true, message: 'QuoteMaker started writing this quote. The items are locked until it finishes.' };
        } catch (err: any) {
            const message = err?.message || 'Writing couldn\'t start.';
            propsRef.current.setToast({ message, type: 'error' });
            return { ok: false, message };
        } finally {
            setBusy(false);
        }
    }, [busy, beginFollowing]);

    // Who writes the job (O1): the builder by hand, or back to the Quote Brain. Saved first, then the start state is read
    // again, so a job handed back is reported and started like any other.
    const changeWriter = useCallback(async (mode: string): Promise<QuoteMakerWriterOutcome> => {
        const id = projectRef.current;
        const change = propsRef.current.onWriterChange;
        if (!id || !change || busy) return BUSY_OUTCOME;
        setBusy(true);
        try {
            const saved = await change(mode);
            if (!saved) {
                const message = 'The job could not be saved, so who writes it did not change on the server. Save it and try again.';
                propsRef.current.setToast({ message, type: 'error' });
                return { ok: false, message };
            }
            if (projectRef.current === id) await followLatest(id);
            return mode === PROJECT_WORKFLOW_MODES.FAST_TRACK_MANUAL_SCOPE
                ? { ok: true, message: 'You write this job\'s scope yourself, so QuoteMaker does not start on it.' }
                : { ok: true, message: 'QuoteMaker writes this job\'s quote again. It starts by itself on a job with no items; on a job with items, ask it to write again.' };
        } catch {
            /* the bar reads the start state again on its next look */
            return { ok: false, message: 'Who writes the job could not be changed. Try again.' };
        } finally {
            setBusy(false);
        }
    }, [busy, followLatest]);

    const stop = useCallback(async (): Promise<QuoteMakerWriterOutcome> => {
        if (!detail) return { ok: false, message: 'QuoteMaker is not writing this quote.' };
        setBusy(true);
        try {
            const { run: stopped } = await stopQuoteBrainRun(detail.run.id);
            setDetail((d) => (d ? { ...d, run: stopped } : d));
            if (!LIVE_RUN_STATUSES.has(stopped.status)) {
                noteQuoteBrainWorking(stopped.projectId, null);
                markQuoteBrainRunLive(stopped.projectId, false);
                return { ok: true, message: 'QuoteMaker stopped writing this quote. The job is as it was.' };
            }
            beginFollowing(stopped);
            return { ok: true, message: 'QuoteMaker is stopping. The job stays as it was.' };
        } catch (err: any) {
            const message = err?.message || 'The run could not be stopped.';
            propsRef.current.setToast({ message, type: 'error' });
            return { ok: false, message };
        } finally {
            setBusy(false);
        }
    }, [detail, beginFollowing]);

    const resume = useCallback(async (): Promise<QuoteMakerWriterOutcome> => {
        if (!detail) return { ok: false, message: 'There is no run of QuoteMaker on this job to carry on.' };
        setBusy(true);
        try {
            const { run: resumed } = await continueQuoteBrainRun(detail.run.id);
            beginFollowing(resumed);
            return { ok: true, message: 'QuoteMaker carries on writing this quote. The items are locked until it finishes.' };
        } catch (err: any) {
            const message = err?.message || 'The run could not continue.';
            propsRef.current.setToast({ message, type: 'error' });
            return { ok: false, message };
        } finally {
            setBusy(false);
        }
    }, [detail, beginFollowing]);

    const adopt = useCallback(async (): Promise<QuoteMakerWriterOutcome> => {
        if (!detail) return NOTHING_HELD_OUTCOME;
        const runId = detail.run.id;
        setBusy(true);
        try {
            if (!(await propsRef.current.ensureSaved())) {
                const message = 'The job could not be saved first, so nothing was replaced.';
                propsRef.current.setToast({ message, type: 'error' });
                return { ok: false, message };
            }
            const token = propsRef.current.getSaveToken();
            if (!token) {
                const message = 'This tab does not know which version of the job it holds. Reload the job and compare again.';
                propsRef.current.setToast({ message, type: 'error' });
                return { ok: false, message };
            }
            const result = await adoptQuoteBrainRun(runId, token);
            deliveredRef.current = runId;
            dismiss(runId);
            setCompareOpen(false);
            setDetail((d) => (d && d.candidate ? { ...d, candidate: { ...d.candidate, delivery: 'written', decision: 'adopted' } } : d));
            const kept = Number(result?.kept) || 0;
            try {
                await propsRef.current.reloadProject();
            } catch {
                setReloadPending({ runId, writtenAt: null });
                const message = 'The new version is now your quote, but this tab couldn\'t load it. Press Load it above.';
                propsRef.current.setToast({ message, type: 'error' });
                return { ok: true, message };
            }
            const message = `The new version is now your quote${kept ? `, with ${kept} of your rows kept` : ''}. Review it in Stage 4 and Stage 5.`;
            propsRef.current.setToast({ message, type: 'success' });
            return { ok: true, message };
        } catch (err: any) {
            const message = err?.message || 'The new version couldn\'t be used.';
            propsRef.current.setToast({ message, type: 'error' });
            return { ok: false, message };
        } finally {
            setBusy(false);
        }
    }, [detail]);

    const keepMine = useCallback(async (): Promise<QuoteMakerWriterOutcome> => {
        if (!detail) return NOTHING_HELD_OUTCOME;
        const runId = detail.run.id;
        setBusy(true);
        try {
            if (!(await propsRef.current.ensureSaved())) {
                const message = 'The job could not be saved first, so nothing was decided. Save it and try again.';
                propsRef.current.setToast({ message, type: 'error' });
                return { ok: false, message };
            }
            const result = await keepMineQuoteBrainRun(runId);
            dismiss(runId);
            setCompareOpen(false);
            setDetail((d) => (d && d.candidate ? { ...d, candidate: { ...d.candidate, decision: 'kept_mine' } } : d));
            if (result?.released) {
                markQuoteBrainJobReleased(projectRef.current, true);
                await propsRef.current.reloadProject();
            }
            const message = 'Kept your quote. The new version stays on record.';
            propsRef.current.setToast({ message, type: 'success' });
            return { ok: true, message };
        } catch (err: any) {
            const message = err?.message || 'Your choice could not be saved. Try again.';
            propsRef.current.setToast({ message, type: 'error' });
            return { ok: false, message };
        } finally {
            setBusy(false);
        }
    }, [detail]);

    // A job QuoteMaker could not finish, handed to the builder: the release unstamps it (write-project.js releaseStamp), so
    // it prices from the business's own rules again, and the job is marked written by hand so the server leaves it alone.
    // This was "Use the old pipeline" until the old pipeline went (docs/old-pipeline-removal/PLAN.md).
    const writeByHand = useCallback(async (): Promise<QuoteMakerWriterOutcome> => {
        if (!detail) return { ok: false, message: 'There is no QuoteMaker run on this job to hand over.' };
        const runId = detail.run.id;
        setBusy(true);
        try {
            if (!(await propsRef.current.ensureSaved())) {
                const message = 'The job could not be saved first, so it stays as it is. Save it and try again.';
                propsRef.current.setToast({ message, type: 'error' });
                return { ok: false, message };
            }
            await releaseQuoteBrainRun(runId);
            markQuoteBrainJobReleased(projectRef.current, true);
            dismiss(runId);
            setDetail(null);
            await propsRef.current.reloadProject();
            await propsRef.current.onWriterChange?.(PROJECT_WORKFLOW_MODES.FAST_TRACK_MANUAL_SCOPE);
            const message = 'You write this job\'s scope yourself. Continue to the Scope Builder.';
            propsRef.current.setToast({ message, type: 'success' });
            return { ok: true, message };
        } catch (err: any) {
            const message = err?.message || 'The job could not be handed to you.';
            propsRef.current.setToast({ message, type: 'error' });
            return { ok: false, message };
        } finally {
            setBusy(false);
        }
    }, [detail]);

    // One way on: load the Quote Brain's quote. Nothing here replaces it (W1 to W3).
    const loadNow = useCallback(() => {
        if (!pending) return;
        const { runId, writtenAt } = pending;
        if (propsRef.current.canReloadNow()) { void loadWritten(runId, writtenAt); return; }
        propsRef.current.showConfirm({
            title: 'Load your written quote?',
            message: QUOTE_BRAIN_WROTE_WHILE_EDITING,
            confirmText: 'Load it',
            cancelText: 'Not yet',
            confirmColor: 'orange',
            icon: 'warning',
            defaultFocus: 'cancel',
            onConfirm: () => { void loadWritten(runId, writtenAt); },
        });
    }, [pending, loadWritten]);

    // The Assistant's QuoteMaker actions on this job (services/quotemaker-writer-commands.ts): the same handlers a click
    // runs, read through a ref so the listener never holds a stale one.
    const commandsRef = useRef<(detail: QuoteMakerWriterCommandDetail) => Promise<QuoteMakerWriterOutcome>>(async () => BUSY_OUTCOME);
    commandsRef.current = async ({ action, runId }) => {
        const r = detail?.run || null;
        const isLive = !!r && LIVE_RUN_STATUSES.has(r.status);
        if (busy) return BUSY_OUTCOME;
        if (action === 'stop') {
            if (!r || !(isLive || r.status === 'paused')) return { ok: false, message: 'QuoteMaker is not writing this quote, so there is nothing to stop.' };
            if (r.stopRequestedAt) return { ok: true, message: 'QuoteMaker is already stopping. The job stays as it was.' };
            return stop();
        }
        if (action === 'start') {
            if (isLive) return { ok: false, message: 'QuoteMaker is already writing this quote.' };
            if (r && (r.status === 'paused' || r.status === 'failed')) return resume();
            if (status?.mode !== 'on' || !status.ready) {
                return { ok: false, message: status?.refusal?.message || 'QuoteMaker cannot write quotes for this business right now.' };
            }
            if (manual) return { ok: false, message: 'You write this job yourself, so QuoteMaker does not start on it. Hand it back to QuoteMaker first.' };
            return start();
        }
        if (action === 'adopt' || action === 'keep_mine') {
            if (!r || !held) return NOTHING_HELD_OUTCOME;
            if (runId && r.id !== runId) return { ok: false, message: 'The version waiting for you changed since this was proposed. Compare again, then choose.' };
            return action === 'adopt' ? adopt() : keepMine();
        }
        if (action === 'write_by_hand') {
            if (isLive) return { ok: false, message: 'QuoteMaker is writing this quote now. Stop it first, then write it yourself.' };
            if (manual) return { ok: true, message: 'You already write this job yourself.' };
            if (r && stamped && (r.status === 'failed' || r.status === 'cancelled')) return writeByHand();
            if (newJob && onWriterChange) return changeWriter(PROJECT_WORKFLOW_MODES.FAST_TRACK_MANUAL_SCOPE);
            return { ok: false, message: 'QuoteMaker already wrote this quote, and its items are yours to change in Stage 4 and Stage 5.' };
        }
        if (action === 'hand_back') {
            if (!manual || !onWriterChange) return { ok: false, message: 'QuoteMaker already writes this job.' };
            return changeWriter(PROJECT_WORKFLOW_MODES.FULL_AI);
        }
        return { ok: false, message: 'That QuoteMaker action is not known.' };
    };
    useEffect(() => {
        const onCommand = (event: Event) => {
            const command = (event as CustomEvent<QuoteMakerWriterCommandDetail>).detail;
            if (!command || !projectRef.current || String(command.projectId) !== String(projectRef.current)) return;
            command.completion = commandsRef.current(command);
        };
        window.addEventListener(QUOTEMAKER_WRITER_COMMAND_EVENT, onCommand);
        return () => window.removeEventListener(QUOTEMAKER_WRITER_COMMAND_EVENT, onCommand);
    }, []);

    if (!projectId) return null;
    const dismissed = run ? readDismissed().includes(run.id) : false;
    const canStart = status?.mode === 'on' && status.ready && !live && stage === 1;

    let body: React.ReactNode = null;
    let panel: React.ReactNode = null;
    // Hover help on every button (Peter, 2026-11-01), shown by TooltipManager below the bar so it never covers the nav.
    const tip = (text: string) => ({ 'data-tooltip': text, 'data-tooltip-position': 'bottom' });
    const STOP_TIP = 'Stop writing this quote. The job stays as it was before the writing started.';
    if (unreachable) {
        body = (
            <>
                <span className="qb-bar__lead">Your quote</span>
                <span className="qb-bar__text" data-testid="quote-brain-unreachable">Can't reach the run right now. You can edit again.</span>
            </>
        );
    } else if (run && live) {
        // The one notice while the Quote Brain writes (O1): Stage 4 and Stage 5 leave the items alone meanwhile. The run's
        // turns and spend are Peter's report page's, not the builder's. The journey line shows the step the run's own tool
        // calls have reached, and its words say what the last of them did (lucid 019 O1).
        // While it writes, a soft band of light crosses the bar and each new step rolls in (O2, Peter 2026-11-02, "Keep both"
        // with the journey line): the step's own span is keyed by its words, so a new step remounts it and replays the roll-in.
        const journey = quoteBrainJourney({ detail });
        body = (
            <>
                <span className="qb-bar__sweep" aria-hidden="true" data-testid="quote-brain-writing-sweep" />
                <span className="qb-bar__lead">Your quote</span>
                <QuoteBrainJourney model={journey} />
                <span className="qb-bar__text" data-testid="quote-brain-writing">
                    {run.stopRequestedAt
                        ? 'Stopping.'
                        : <><span key={journey.activity} className="qb-bar__step" data-testid="quote-brain-writing-step">{journey.activity}</span>. {stage >= 4 ? "You can edit when it's done." : 'You can close the tab.'}</>}
                </span>
                <span className="qm-chip qb-bar__figure">{elapsed(run.startedAt || run.createdAt, null, now)}</span>
                <button type="button" className="qm-inline-action-btn qm-btn qm-btn--secondary qb-bar__action" disabled={busy || !!run.stopRequestedAt} onClick={() => { void stop(); }} {...tip(STOP_TIP)}>Stop</button>
            </>
        );
    } else if (pending) {
        body = (
            <>
                <span className="qb-bar__lead">Your quote</span>
                <span className="qb-bar__text">The quote is written, but this tab shows the old version.</span>
                <button type="button" className="qm-inline-action-btn qm-btn qm-btn--primary qb-bar__action" onClick={loadNow} data-testid="quote-brain-load-written" {...tip('Load the quote that was written into this tab.')}>Load it</button>
            </>
        );
    } else if (run && held) {
        if (compareOpen && compare) {
            panel = (
                <QuoteBrainComparePanel
                    comparison={compare}
                    busy={busy}
                    onAdopt={() => { void adopt(); }}
                    onKeepMine={() => { void keepMine(); }}
                    onClose={() => setCompareOpen(false)}
                />
            );
        } else if (compareError) {
            body = (
                <>
                    <span className="qb-bar__lead">Your quote</span>
                    <span className="qb-bar__text">A new version is ready, but the comparison didn't load: {compareError}</span>
                    <button type="button" className="qm-inline-action-btn qm-btn qm-btn--primary qb-bar__action" onClick={() => setCompareTry((n) => n + 1)} data-testid="quote-brain-compare-retry" {...tip('Read the comparison again.')}>Try again</button>
                </>
            );
        } else {
            body = (
                <>
                    <span className="qb-bar__lead">Your quote</span>
                    <span className="qb-bar__text">{detail?.candidate?.holdReason || 'A new version is ready to compare.'}</span>
                    <button type="button" className="qm-inline-action-btn qm-btn qm-btn--primary qb-bar__action" disabled={!compare} onClick={() => setCompareOpen(true)} data-testid="quote-brain-compare-open" {...tip('See the new quote beside yours, then choose which one to keep.')}>Compare</button>
                </>
            );
        }
    } else if (run && (run.status === 'paused'
        // A stamped job's way back always shows: without it the job has no path.
        || (stamped && (run.status === 'failed' || run.status === 'cancelled'))
        || (!dismissed && run.status === 'failed'))) {
        body = (
            <>
                <span className="qb-bar__lead">Your quote</span>
                <span className="qb-bar__text" data-testid="quote-brain-stopped">
                    {quoteMakerStoppedText(run, { showCosts })}
                </span>
                {run.status !== 'cancelled' && (
                    <button type="button" className="qm-inline-action-btn qm-btn qm-btn--primary qb-bar__action" disabled={busy} onClick={() => { void resume(); }}
                        {...tip(run.status === 'paused' ? 'Carry on writing this quote from where it paused.' : 'Start writing this quote again.')}>{run.status === 'paused' ? 'Continue' : 'Try again'}</button>
                )}
                {run.status === 'paused' && (
                    <button type="button" className="qm-inline-action-btn qm-btn qm-btn--secondary qb-bar__action" disabled={busy || !!run.stopRequestedAt} onClick={() => { void stop(); }} data-testid="quote-brain-stop-paused" {...tip(STOP_TIP)}>Stop</button>
                )}
                {stamped && run.status !== 'paused' && (
                    <button type="button" className="qm-inline-action-btn qm-btn qm-btn--secondary qb-bar__action" disabled={busy} onClick={() => { void writeByHand(); }} data-testid="quote-brain-release"
                        {...tip(WRITE_IT_YOURSELF_TIP)}>Write it yourself</button>
                )}
                {!stamped && run.status === 'failed' && (
                    <button type="button" className="qm-inline-action-btn qm-btn qm-btn--secondary qb-bar__action" onClick={() => { dismiss(run.id); setDetail(null); }} {...tip('Hide this message.')}>Dismiss</button>
                )}
            </>
        );
    } else if (run && finished?.runId === run.id) {
        // The quote is in: the whole line fills, with how many items and how long it took, and the way to them.
        const took = run.startedAt && run.finishedAt ? elapsed(run.startedAt, run.finishedAt, now) : '';
        const items = itemCount > 0 ? `${itemCount} item${itemCount === 1 ? '' : 's'}` : '';
        body = (
            <>
                <span className="qb-bar__lead">Your quote</span>
                <QuoteBrainJourney model={quoteBrainJourney({ detail, delivered: true })} />
                <span className="qb-bar__text" data-testid="quote-brain-finished">
                    {`Quote ready${items ? `: ${items}` : ''}${took ? `, written in ${took}` : ''}.`}
                </span>
                {stage < 4 && onOpenScope && (
                    <button type="button" className="qm-inline-action-btn qm-btn qm-btn--primary qb-bar__action" onClick={() => onOpenScope()} data-testid="quote-brain-open-scope"
                        {...tip('Go to Stage 4 to see and change the items.')}>
                        Open the scope
                    </button>
                )}
                <button type="button" className="qm-inline-action-btn qm-btn qm-btn--quiet qb-bar__action qb-bar__dismiss" aria-label="Dismiss" onClick={() => setFinished(null)}
                    {...tip('Hide this message.')}>
                    <Icons.X size={14} aria-hidden="true" />
                </button>
            </>
        );
    } else if (canStart) {
        // A job that already has a quote does not need another one to move on, so the button steps down to secondary.
        const again = !newJob && hasQuoteWork;
        // On a new job the server starts the run by itself (the live tick), so nothing waits for a press: the bar says how
        // far it is and shows no button (Peter, 2026-10-31: "I still feel like I need to press Write now"). A "Write now"
        // there would also start from files still being read. When the tick's start failed (it retries each minute, then
        // gives up), or the server holds no summary for the job, the bar says why, the button is the way on, and Stage 1
        // keeps Analyse.
        const stalled = autoStart?.state === 'expected' && noSignal === projectId;
        const startsItself = newJob && !manual && !stalled && (autoStart?.state === 'expected' || autoStart?.state === 'waiting');
        // Written by hand (O1): the server leaves it alone, and the bar offers to hand it back.
        const byHand = newJob && manual && !!onWriterChange;
        const startFailed = newJob && (autoStart?.state === 'retrying' || autoStart?.state === 'gave_up');
        const why = String(autoStart?.reason || '').trim();
        // Short helper words (Peter, 2026-11-01: the old ones were "weird, confusing and convoluted"). The lead already says
        // what this is, and the button says what to press.
        const failedText = `Couldn't start${autoStart?.state === 'retrying' ? ' yet' : ''}`
            + `${why ? `: ${/[.!?]$/.test(why) ? why : `${why}.`}` : '.'}`
            + `${autoStart?.state === 'retrying' ? ' Trying again every minute.' : ''}`;
        // On its way by itself: the journey line from this tab's reading to the server's start (lucid 019 O1). The Files and
        // Summary steps happen in this tab, so it stays open until the server has the job.
        const journey = startsItself ? quoteBrainJourney({
            files,
            filesProcessing: filesProcessing ?? (preparing && !summarizing),
            summarizing,
            hasSummary,
            autoStart: autoStart?.state || null,
        }) : null;
        body = (
            <>
                <span className="qb-bar__lead">Your quote</span>
                {journey && <QuoteBrainJourney model={journey} />}
                <span className="qb-bar__text" data-testid={startsItself ? 'quote-brain-starts-itself' : byHand ? 'quote-brain-by-hand' : undefined}>
                    {byHand
                        ? 'You\'re writing this scope yourself.'
                        : journey
                        ? (!journey.moving
                            ? 'Starts writing once the files are read.'
                            : `${journey.activity}. ${journey.onServer ? 'You can close the tab.' : 'Keep this tab open until writing starts.'}`)
                        : startFailed
                            ? failedText
                            : again
                                ? 'Writes a second version to compare. Nothing changes until you choose.'
                                : 'Writes the whole quote from the job\'s files.'}
                </span>
                {startsItself && onWriterChange && (
                    <button type="button" className="qm-inline-action-btn qm-btn qm-btn--quiet qb-bar__action" disabled={busy}
                        onClick={() => { void changeWriter(PROJECT_WORKFLOW_MODES.FAST_TRACK_MANUAL_SCOPE); }} data-testid="quote-brain-by-hand-choose"
                        {...tip(WRITE_IT_YOURSELF_TIP)}>
                        Write it yourself
                    </button>
                )}
                {byHand && (
                    <button type="button" className="qm-inline-action-btn qm-btn qm-btn--secondary qb-bar__action" disabled={busy}
                        onClick={() => { void changeWriter(PROJECT_WORKFLOW_MODES.FULL_AI); }} data-testid="quote-brain-hand-back"
                        {...tip(HAND_BACK_TIP)}>
                        Write it for me
                    </button>
                )}
                {!startsItself && !byHand && (
                    <button type="button" className={`qm-inline-action-btn qm-btn ${again ? 'qm-btn--secondary' : 'qm-btn--primary'} qb-bar__action`} disabled={busy} onClick={() => { void start(); }} data-testid="quote-brain-write"
                        {...tip(again ? "Write a second version from the job's files. You compare the two before anything changes." : "Start writing this job's quote now, from its files. You can close the tab while it works.")}>
                        {again ? 'Write again' : 'Write the quote'}
                    </button>
                )}
            </>
        );
    }
    if (panel) return <div className="qb-bar-wrap" data-testid="quote-brain-bar">{panel}</div>;
    if (!body) return null;
    return (
        <div role="banner" aria-label="Quote progress" className={`qb-bar${live ? ' qb-bar--live' : ''}`} data-testid="quote-brain-bar">
            {body}
        </div>
    );
}

export default QuoteBrainBar;
